import { useEffect, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import * as Yup from "yup";
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import InputField from '../../components/inputFields/InputField';
import Button from '../../components/buttons/Button';
import { exportAccountConsultationInvoice, exportAccountMedicineCenterReport, exportAccountMedicineInvoice, exportAccountPackageInvoice } from '../../services/account/account_home';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { getUserName } from '../../utils/localStoarege';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import { getCenters } from '../../services/clinic/centers';

function AccountDownloadExcel() {
    const [centers, setCenters] = useState([]);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    const todayDate = formatDate(today);
    const threeMonthsAgoDate = formatDate(threeMonthsAgo);

    const centerOptions = centers
        .map((center) => {
            const rawName = center?.name ?? center?.center_name ?? center?.centre_name;
            const normalizedName = typeof rawName === "string" ? rawName : "";
            if (!normalizedName) return null;
            return {
                value: normalizedName,
                label: normalizedName,
            };
        })
        .filter(Boolean);

    const getErrorMessage = (error, fallback = "Oops! Something went wrong, please try again.") => {
        const apiError =
            error?.response?.data?.error ??
            error?.response?.data?.detail ??
            error?.message;

        if (typeof apiError === "string" && apiError.trim()) return apiError;
        if (Array.isArray(apiError)) return apiError.filter(Boolean).join(", ") || fallback;
        if (apiError && typeof apiError === "object") {
            const flattened = Object.values(apiError)
                .flat()
                .filter(Boolean)
                .map((item) => (typeof item === "string" ? item : String(item)))
                .join(", ");
            return flattened || fallback;
        }
        return fallback;
    };

    const handleSubmitPackageInvoice = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const response = await exportAccountPackageInvoice(getUserName(), values.from_date, values.to_date);
            if (response?.status === 200) {
                toast.success("Excel Downloaded");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitConsultationInvoice = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const response = await exportAccountConsultationInvoice(getUserName(), values.from_date, values.to_date);
            if (response?.status === 200) {
                toast.success("Excel Downloaded");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitMedicineInvoice = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const response = await exportAccountMedicineInvoice(getUserName(), values.from_date, values.to_date);
            if (response?.status === 200) {
                toast.success("Excel Downloaded");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitMedicineCenterReport = async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            const response = await exportAccountMedicineCenterReport(values.from_date, values.to_date, values.center_name);
            if (response?.status === 200) {
                toast.success("Excel Downloaded");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    const fetchCenters = async () => {
        try {
            const res = await getCenters("?page_size=100");
            if (res?.status === 200) {
                setCenters(res.data.results || []);
            } else {
                toast.error("Unable to fetch centers");
            }
        } catch (error) {
            toast.error("Unable to fetch centers");
        }
    };

    useEffect(() => {
        fetchCenters();
    }, []);

    const validationSchema = Yup.object({
        from_date: Yup.string()
            .required("From is required")
            .test(
                "from-date-last-3-months",
                "From date must be within last 3 months",
                (value) => !!value && value >= threeMonthsAgoDate && value <= todayDate
            ),
        to_date: Yup.string()
            .required("To is required")
            .test(
                "to-date-last-3-months",
                "To date must be within last 3 months",
                (value) => !!value && value >= threeMonthsAgoDate && value <= todayDate
            )
            .test(
                "to-after-from",
                "To date must be greater than or equal to From date",
                function (value) {
                    const { from_date } = this.parent;
                    if (!from_date || !value) return true;
                    return value >= from_date;
                }
            ),
    });

    const centerReportValidationSchema = Yup.object({
        center_name: Yup.string().required("Center is required"),
        from_date: Yup.string()
            .required("From is required")
            .test(
                "from-date-last-3-months",
                "From date must be within last 3 months",
                (value) => !!value && value >= threeMonthsAgoDate && value <= todayDate
            ),
        to_date: Yup.string()
            .required("To is required")
            .test(
                "to-date-last-3-months",
                "To date must be within last 3 months",
                (value) => !!value && value >= threeMonthsAgoDate && value <= todayDate
            )
            .test(
                "to-after-from",
                "To date must be greater than or equal to From date",
                function (value) {
                    const { from_date } = this.parent;
                    if (!from_date || !value) return true;
                    return value >= from_date;
                }
            ),
    });

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', },
            { name: 'Download Excel', },
        ]} />
        <div className="py-3 w-full">
            <Formik
                initialValues={{
                    from_date: "",
                    to_date: "",
                    center: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmitPackageInvoice}
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className="space-y-4 my-4 flex flex-col">
                        <StyledHeadingText text={"Package & Consultation Invoice"} />
                        <div className="flex flex-row justify-start gap-4 w-full bg-white rounded-lg shadow-lg p-4 border">
                            <div className="flex-1">
                                <InputField
                                    label="From Date"
                                    name="from_date"
                                    type="date"
                                    min={threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="To Date"
                                    name="to_date"
                                    type="date"
                                    min={values.from_date || threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="bg-primary text-white px-4 py-2 rounded max-w-32"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Please Wait..." : "Download"}
                        </Button>
                    </Form>
                )}
            </Formik>
        </div>
        <div className="py-3 w-full">
            <Formik
                initialValues={{
                    from_date: "",
                    to_date: "",
                    center: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmitConsultationInvoice}
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className="space-y-4 my-4 flex flex-col">
                        <StyledHeadingText text={"Consultation Invoice"} />
                        <div className="flex flex-row justify-start gap-4 w-full bg-white rounded-lg shadow-lg p-4 border">
                            <div className="flex-1">
                                <InputField
                                    label="From Date"
                                    name="from_date"
                                    type="date"
                                    min={threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="To Date"
                                    name="to_date"
                                    type="date"
                                    min={values.from_date || threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="bg-primary text-white px-4 py-2 rounded max-w-32"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Please Wait..." : "Download"}
                        </Button>
                    </Form>
                )}
            </Formik>
        </div>

        <div className="py-3 w-full">
            <Formik
                initialValues={{
                    from_date: "",
                    to_date: "",
                    center: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmitMedicineInvoice}
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className="space-y-4 my-4 flex flex-col">
                        <StyledHeadingText text={"Medicine Invoice"} />
                        <div className="flex flex-row justify-start gap-4 w-full bg-white rounded-lg shadow-lg p-4 border">
                            <div className="flex-1">
                                <InputField
                                    label="From Date"
                                    name="from_date"
                                    type="date"
                                    min={threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="To Date"
                                    name="to_date"
                                    type="date"
                                    min={values.from_date || threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="bg-primary text-white px-4 py-2 rounded max-w-32"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Please Wait..." : "Download"}
                        </Button>
                    </Form>
                )}
            </Formik>
        </div>
        <div className="py-3 w-full">
            <Formik
                initialValues={{
                    center_name: "",
                    from_date: "",
                    to_date: "",
                }}
                validationSchema={centerReportValidationSchema}
                onSubmit={handleSubmitMedicineCenterReport}
            >
                {({ isSubmitting, values }) => (
                    <Form className="space-y-4 my-4 flex flex-col">
                        <StyledHeadingText text={"Medicine Report (Center)"} />
                        <div className="flex flex-row justify-start gap-4 w-full bg-white rounded-lg shadow-lg p-4 border">
                            <div className="flex-1">
                                <FormikDropdown
                                    label="Center"
                                    name="center_name"
                                    placeholder="Select Center"
                                    options={centerOptions}
                                    needSearch
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="From Date"
                                    name="from_date"
                                    type="date"
                                    min={threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="To Date"
                                    name="to_date"
                                    type="date"
                                    min={values.from_date || threeMonthsAgoDate}
                                    maxDate={todayDate}
                                    placeholder="Select Date"
                                    required
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            className="bg-primary text-white px-4 py-2 rounded max-w-32"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Please Wait..." : "Download"}
                        </Button>
                    </Form>
                )}
            </Formik>
        </div>
    </>);
}

export default AccountDownloadExcel;